import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import rehypeHighlight from 'rehype-highlight';
import { HiArrowLeft, HiArrowRight } from 'react-icons/hi';
import { FiArrowUpRight, FiEye } from 'react-icons/fi';
import { getPostBySlug, getRelatedPosts } from '../../posts';
import { author, getTopic, socials } from './config';
import { formatDate, extractHeadings } from './utils';
import { useViews } from './useViews';
import BlogHeader from './BlogHeader';
import BlogFooter from './BlogFooter';
import './blog.css';

const Post = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const viewCount = useViews(slug, post?.views ?? 0);
  const headings = useMemo(
    () => (post ? extractHeadings(post.content) : []),
    [post]
  );
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = post ? `${post.title} — Jonah Odoh` : 'Post not found';
  }, [post]);

  // Scroll-spy: highlight the TOC entry for the section currently in view.
  useEffect(() => {
    if (!headings.length) return undefined;
    const offset = 120; // a little below the sticky nav
    const onScroll = () => {
      let current = headings[0].id;
      for (const h of headings) {
        const el = document.getElementById(h.id);
        if (el && el.getBoundingClientRect().top <= offset) current = h.id;
      }
      setActiveId(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [headings]);

  if (!post) {
    return (
      <div className="blog__page">
        <BlogHeader />
        <div className="container blog__empty">
          <h2>Post not found</h2>
          <p>That post doesn’t exist (or moved).</p>
          <Link to="/blog" className="post__back">
            <HiArrowLeft /> Back to all posts
          </Link>
        </div>
        <BlogFooter />
      </div>
    );
  }

  const topic = getTopic(post.category);
  const related = getRelatedPosts(post.slug, 3);

  const shareUrl =
    typeof window !== 'undefined' ? window.location.href : '';
  const xShare = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    post.title
  )}&url=${encodeURIComponent(shareUrl)}`;
  const liShare = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
    shareUrl
  )}`;

  return (
    <div className="blog__page">
      <BlogHeader />

      {/* ── ARTICLE HERO ── */}
      <header className="post__hero">
        <div className="container post__hero-inner">
          <div className="post__hero-meta">
            <span>NO. {String(post.number).padStart(2, '0')}</span>
            <span className="post__hero-cat">● {topic ? topic.name : post.category}</span>
            <span>{formatDate(post.date, true)}</span>
            <span>{post.readTime}</span>
            {post.views != null && (
              <span className="post__hero-views">
                <FiEye /> {viewCount.toLocaleString()} views
              </span>
            )}
          </div>

          <h1 className="post__hero-title">{post.title}</h1>
          <p className="post__hero-excerpt">{post.excerpt}</p>

          <div className="post__byline">
            <div className="post__byline-author">
              <img src={author.avatar} alt={author.name} />
              <div>
                <span className="post__byline-name">By {author.name}</span>
                <span className="post__byline-role">{author.role}</span>
              </div>
            </div>
            <div className="post__share">
              <a href={xShare} target="_blank" rel="noopener noreferrer">
                Share on X <FiArrowUpRight />
              </a>
              <a href={liShare} target="_blank" rel="noopener noreferrer">
                LinkedIn <FiArrowUpRight />
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* ── BODY + TOC ── */}
      <div className="container post__body">
        {headings.length > 0 && (
          <aside className="post__toc">
            <span className="post__toc-label">Contents</span>
            <ul>
              {headings.map((h) => (
                <li key={h.id}>
                  <a
                    href={`#${h.id}`}
                    className={h.id === activeId ? 'active' : ''}
                  >
                    {h.text}
                  </a>
                </li>
              ))}
            </ul>
          </aside>
        )}

        <article className="post__content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeSlug, rehypeHighlight]}
          >
            {post.content}
          </ReactMarkdown>

          <div className="post__end">
            <div className="post__end-author">
              <img src={author.avatar} alt={author.name} />
              <div>
                <strong>{author.name}</strong>
                <span>{author.role}</span>
              </div>
            </div>
            <a href={socials.email} className="btn outline sm">
              Get in touch <FiArrowUpRight />
            </a>
          </div>
        </article>
      </div>

      {/* ── RELATED ── */}
      {related.length > 0 && (
        <section className="container post__related">
          <div className="post__related-head">
            <h2>More from the blog</h2>
            <Link to="/blog" className="post__related-all">
              See all <HiArrowRight />
            </Link>
          </div>
          <div className="blog__grid">
            {related.map((r) => {
              const rTopic = getTopic(r.category);
              return (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="blog__card">
                  <div className="blog__card-meta">
                    <span className="blog__card-cat">
                      {rTopic ? rTopic.name : r.category}
                    </span>
                    <span className="blog__card-dot">•</span>
                    <span>{formatDate(r.date)}</span>
                  </div>
                  <h3>{r.title}</h3>
                  <p className="blog__card-excerpt">{r.excerpt}</p>
                  <div className="blog__card-foot">
                    <span>{r.readTime}</span>
                    <span className="blog__card-more">
                      Read <HiArrowRight />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <BlogFooter />
    </div>
  );
};

export default Post;
